import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Eye, Tag } from "lucide-react";
import { Button } from "../components/ui/button";
import { NSBLogo } from "../components/NSBLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts/${postId}`)
      .then(r => r.json())
      .then(d => setPost(d.post))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#020817] text-white flex flex-col items-center justify-center">
        <p className="text-white/60 mb-4">Post not found</p>
        <Link to="/"><Button variant="outline" className="border-white/20 text-white">Back to Home</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      <nav className="border-b border-white/5 backdrop-blur-xl bg-black/40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <NSBLogo className="h-10" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-12">
        {post.featured_image_url && (
          <img
            src={post.featured_image_url.startsWith('/') ? `${API_URL}${post.featured_image_url}` : post.featured_image_url}
            alt={post.title}
            className="w-full h-64 sm:h-96 object-cover rounded-xl mb-8"
          />
        )}

        <div className="flex items-center gap-4 text-sm text-white/40 mb-4">
          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {post.views} views</span>
          {post.category && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs"><Tag className="w-3 h-3" /> {post.category}</span>}
        </div>

        <h1 className="text-3xl sm:text-5xl font-black mb-6 leading-tight">{post.title}</h1>

        {post.author_name && (
          <p className="text-white/40 text-sm mb-8">By {post.author_name}</p>
        )}

        <div className="prose prose-invert max-w-none text-white/80 leading-relaxed whitespace-pre-wrap">
          {post.content}
        </div>
      </article>
    </div>
  );
}
